'use client';

import Image from 'next/image';
import { useBootstrap } from '@/lib/utils/clientUtils';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import LanguageSwitcher from '@/components/core/LanguageSwitcher';

const Footer: React.FC = () => {
  const t = useTranslations('common');
  
  // Initialize Bootstrap for any components in the footer
  useBootstrap();

  return (
    <footer className="footer border-top pt-5 pb-4 pb-lg-5">
      <div className="container pt-lg-4">
        <div className="row pb-5">
          {/* Logo and Description Section */}
          <div className="col-12 col-lg-3 mb-4 mb-lg-0">
            <div className="navbar-brand text-dark p-0 me-0 mb-3 mb-lg-4">
              <Link href="/">
                <Image 
                  src="/assets/img/logo.svg" 
                  width={172}
                  height={50}
                  alt={t('global.title')}
                  className="d-block"
                  style={{ width: '172px', height: '50px' }}
                  unoptimized
                />
              </Link>
            </div>
            <p className="fs-sm text-light opacity-70 pb-lg-3 mb-3">
              {t('global.footer.description')}
            </p>
            <LanguageSwitcher />
          </div>

          {/* Navigation Links Section */}
          <div className="col-12 col-lg-9 col-xl-8 offset-lg-0 offset-xl-1 pt-4 pt-lg-0">
            <div id="footer-links" className="row h-lg-100">
              
              {/* Best Spotify Playlists Column */}
              <div className="col-12 col-lg-5 border-end-lg border-secondary-grey border-bottom border-bottom-lg-0 pb-3 pb-lg-0 mb-3 mb-lg-0 text-center d-lg-flex flex-lg-column h-lg-100">
                <h6 className="mb-2 text-uppercase fs-xs">
                  <a 
                    href="#useful-links" 
                    className="d-block text-dark dropdown-toggle d-lg-none py-2" 
                    data-bs-toggle="collapse"
                    aria-expanded="false"
                  >
                    {t('global.footer.spotifyPlaylistsTitle')}
                  </a>
                  <span className="d-none d-lg-block">{t('global.footer.spotifyPlaylistsTitle')}</span>
                </h6>
                <div id="useful-links" className="collapse d-lg-block" data-bs-parent="#footer-links">
                  <ul className="nav flex-column pb-lg-1 mb-lg-3 text-center">
                    <li className="nav-item">
                      <Link href="/playlists" className="nav-link d-block px-0 pt-1 pb-2">
                        {t('global.footer.navigation.main.discoverPlaylists')}
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link href="/playlists/tags" className="nav-link d-block px-0 pt-1 pb-2">
                        {t('global.footer.navigation.main.playlistTags')}
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link href="/playlists/curated-by" className="nav-link d-block px-0 pt-1 pb-2">
                        {t('global.footer.navigation.main.playlistCurators')}
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link href="/playlists/featuring" className="nav-link d-block px-0 pt-1 pb-2">
                        {t('global.footer.navigation.main.featuredArtists')}
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link href="/playlists/genres" className="nav-link d-block px-0 pt-1 pb-2">
                        {t('global.footer.navigation.main.featuredGenres')}
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link href="/playlists/categories" className="nav-link d-block px-0 pt-1 pb-2">
                        {t('global.footer.navigation.main.playlistCategories')}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Social Media Column */}
              <div className="col-12 col-lg-3 col-xl-3 border-end-lg border-secondary-grey border-bottom border-bottom-lg-0 pb-3 pb-lg-0 mb-3 mb-lg-0 text-center d-lg-flex flex-lg-column h-lg-100">
                <h6 className="mb-2 text-uppercase fs-xs">
                  <a 
                    href="#social-links" 
                    className="d-block text-dark dropdown-toggle d-lg-none py-2" 
                    data-bs-toggle="collapse"
                    aria-expanded="false"
                  >
                    {t('global.footer.socialMediaTitle')}
                  </a>
                  <span className="d-none d-lg-block">{t('global.footer.socialMediaTitle')}</span>
                </h6>
                <div id="social-links" className="collapse d-lg-block" data-bs-parent="#footer-links">
                  <ul className="nav flex-column mb-2 mb-lg-0 text-center">
                    <li className="nav-item">
                      <a 
                        href={`https://instagram.com/${t('global.contact.instaUsername')}`} 
                        className="nav-link d-block px-0 pt-1 pb-2"
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <i className="bx bxl-instagram fs-5 me-2"></i>
                        Instagram
                      </a>
                    </li>
                    <li className="nav-item">
                      <a 
                        href={t('global.contact.spotifyProfileUrl')} 
                        className="nav-link d-block px-0 pt-1 pb-2"
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <i className="bx bxl-spotify fs-5 me-2"></i>
                        Spotify
                      </a>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Discover MusicMagic.io Column */}
              <div className="col-12 col-lg-4 col-xl-4 pt-2 pt-lg-0 text-center d-lg-flex flex-lg-column h-lg-100">
                <h6 className="mb-2 text-uppercase fs-xs">
                  <a 
                    href="#info-links" 
                    className="d-block text-dark dropdown-toggle d-lg-none py-2" 
                    data-bs-toggle="collapse"
                    aria-expanded="false"
                  >
                    {t('global.footer.informationTitle')}
                  </a>
                  <span className="d-none d-lg-block">{t('global.footer.informationTitle')}</span>
                </h6>
                <div id="info-links" className="collapse d-lg-block" data-bs-parent="#footer-links">
                  <ul className="nav flex-column mb-2 mb-lg-0 text-center">
                    <li className="nav-item">
                      <Link href="/about/what-is-musicmagic-io" className="nav-link d-block px-0 pt-1 pb-2">
                        {t('global.footer.navigation.about.howItWorks')}
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link href="/contact" className="nav-link d-block px-0 pt-1 pb-2">
                        {t('global.footer.getInTouchTitle')}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom - Copyright (Full Width) */}
      <div className="border-top border-secondary-grey">
        <div className="container pt-4">
          <p className="nav d-block fs-xs text-center text-md-start pb-2 pb-lg-0 mb-0">
            <span className="text-light opacity-50">{t('global.footer.copyright', { year: new Date().getFullYear() })}</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 